import { useMutation, useQuery, useQueryClient } from 'react-query';
import { chamarOktonBank, enviarOktonBank } from '../oktonbank';

// Transferência entre contas próprias. Não é título: tem cadastro próprio no
// IFLEX (transferenciabancaria) e aparece nos dois extratos — a saída na Conta
// de origem e a entrada na Conta de destino.
//
// Por isso tem duas pontas para conciliar, contadas em separado. Com uma Conta
// filtrada, o que interessa é a ponta daquela Conta.
export interface Transferencia {
  id_tba: string;
  filial_tba: string;
  documento_tba: string | null;
  data_tba: string;
  valor_tba: string;
  observacao_tba: string | null;
  origem_tba: string;
  destino_tba: string;
  contaorigem_tba: string | null;
  origemdescricao_tba: string | null;
  contadestino_tba: string | null;
  destinodescricao_tba: string | null;
  // origem_tba e destino_tba guardam a Moeda do banco; a Conta vem dela pelo
  // vínculo. Sem vínculo a Conta não resolve, e é o nome da Moeda que
  // identifica a ponta.
  origemmoeda_tba: string | null;
  destinomoeda_tba: string | null;
  nome_pct: string | null;
  conciliadoorigem_tba: string;
  conciliadodestino_tba: string;
}

interface IResponseData {
  rows: Transferencia[];
}

async function getTransferencias(
  idConta?: string,
  status: 'pending' | 'all' = 'pending',
  dataIni = '',
  dataFim = '',
): Promise<Transferencia[]> {
  const dados = await chamarOktonBank<IResponseData>(
    'OBTransferencia',
    'listar',
    {
      conta: idConta || '',
      status,
      dataini: dataIni,
      datafim: dataFim,
    },
  );
  return Array.isArray(dados?.rows) ? dados.rows : [];
}

export function useTransferencias(
  idConta?: string,
  status: 'pending' | 'all' = 'pending',
  dataIni = '',
  dataFim = '',
  habilitado = true,
) {
  return useQuery(
    ['transferencias', idConta ?? '', status, dataIni, dataFim],
    () => getTransferencias(idConta, status, dataIni, dataFim),
    { enabled: habilitado },
  );
}

// Para uma Conta, diz se aquela ponta da Transferência ainda está por conciliar
// e de que lado ela cai: saindo da Conta é débito, entrando é crédito.
export function pontaDaConta(
  transferencia: Transferencia,
  idConta: string,
): { saida: boolean; conciliada: boolean } | null {
  if (transferencia.contaorigem_tba === idConta) {
    return {
      saida: true,
      conciliada: transferencia.conciliadoorigem_tba !== '0',
    };
  }
  if (transferencia.contadestino_tba === idConta) {
    return {
      saida: false,
      conciliada: transferencia.conciliadodestino_tba !== '0',
    };
  }
  return null;
}

export interface NovaTransferencia {
  documento: string;
  origem: string;
  destino: string;
  // Sem extrato, a tela digita data e valor; partindo da Conciliação eles vêm
  // do lançamento e a Transferência já nasce conciliada.
  data?: string;
  valor?: string;
  planocontas?: string;
  observacao?: string;
}

function invalidarTransferencias(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries('transferencias');
  queryClient.invalidateQueries('monitor-bancario-resumo');
  queryClient.invalidateQueries('conciliacoes');
  queryClient.invalidateQueries('extrato-bancario');
  queryClient.invalidateQueries('dashboard');
}

export function useCreateTransferencia() {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: NovaTransferencia) =>
      enviarOktonBank<{ id_tba: number }>('OBTransferencia', 'cadastrar', {
        ...payload,
      }),
    { onSuccess: () => invalidarTransferencias(queryClient) },
  );
}

export function useDeleteTransferencia() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) =>
      enviarOktonBank('OBTransferencia', 'excluir', { transferencia: id }),
    { onSuccess: () => invalidarTransferencias(queryClient) },
  );
}
